import { Controller } from "stimulus"
import sanitizeHtml from "sanitize-html"

export default class extends Controller {
  connect() {
    this._boundHandleClick = this.#handleClick.bind(this)
    this._boundResetToList = this.#resetToList.bind(this)
    this.element.addEventListener("click", this._boundHandleClick)
    this.element.addEventListener("shown.bs.modal", this._boundResetToList)
  }

  disconnect() {
    if (this._boundHandleClick) {
      this.element.removeEventListener("click", this._boundHandleClick)
    }
    if (this._boundResetToList) {
      this.element.removeEventListener("shown.bs.modal", this._boundResetToList)
    }
  }

  toggleClearButton(event) {
    const input = event.target
    const container = input.closest(".search-input-with-icon")
    if (!container) return
    const clearBtn = container.querySelector(".search-clear-btn")
    if (!clearBtn) return
    clearBtn.classList.toggle("d-none", input.value.length === 0)
  }

  clearSearch(event) {
    const container = event.target.closest(".search-input-with-icon")
    if (!container) return
    const input = container.querySelector("input[type=search]")
    if (!input) return
    input.value = ""
    input.dispatchEvent(new Event("input", { bubbles: true }))
    input.focus()
  }

  async #handleClick(event) {
    const searchBtn = event.target.closest("#search_for_plan_shopping_help")
    if (searchBtn) {
      event.preventDefault()
      await this.#searchForHelp()
      return
    }

    const helpButton = event.target.closest(".help_button")
    if (helpButton) {
      event.preventDefault()
      await this.#requestHelpFor(helpButton)
      return
    }

    const nameSearchOnly = event.target.closest(".name_search_only")
    if (nameSearchOnly) {
      event.preventDefault()
      this.#showSearchForType(nameSearchOnly.id)
      return
    }

    const backToHelp = event.target.closest("#back_to_help")
    if (backToHelp) {
      event.preventDefault()
      this.#backToHelp()
      return
    }

    const selectExpert = event.target.closest(".select-broker, .select-assister")
    if (selectExpert) {
      event.preventDefault()
      await this.#selectMyExpert(selectExpert)
    }
  }

  #resetToList() {
    // Mirrors legacy behavior that ran when opening the modal.
    this.element.querySelectorAll(".help_reset").forEach((el) => {
      el.classList.add("hide")
      el.classList.add("d-none")
    })
    // Re-enable expert-select buttons: global .disabled + duplicate #active_*_tab can otherwise
    // leave a non-first assister's button stuck (bootstrap .disabled) after a prior selection.
    this.element.querySelectorAll(".select-assister, .select-broker").forEach((el) => {
      el.classList.remove("disabled")
    })
    this.#toggleId("help_list", true)
    this.#toggleId("back_to_help", false)
    this.#toggleId("bottom_expert_link", true)
    this.#toggleId("bottom_expert_assister_link", true)
  }

  #showSearchForType(typeId) {
    this.#toggleId("help_list", false)
    this.#toggleId("help_search", true)
    this.#setTextContent("help_type", typeId || "")
    this.#toggleId("back_to_help", true)
  }

  #backToHelp() {
    this.element.querySelectorAll(".help_reset").forEach((el) => el.classList.add("hide"))
    this.#toggleId("back_to_help", false)
    this.#toggleId("help_list", true)
    this.#setTextContent("help_status", "")
    this.#toggleId("help_sign_up_title", true)
    this.#toggleId("help_from_expert_title", false)
  }

  async #searchForHelp() {
    const query = this.#personPayloadSearchParams([
      ["firstname", this.#valueById("help_first_name")],
      ["lastname", this.#valueById("help_last_name")],
      ["type", this.#htmlById("help_type")]
    ])

    const parsed = await this.#getJson(query)
    if (!parsed) return

    this.#setTextContent("help_status", parsed.status || "")
  }

  async #requestHelpFor(buttonEl) {
    const query = this.#personPayloadSearchParams([
      ["assister", buttonEl.getAttribute("data-assister") || ""],
      ["broker", buttonEl.getAttribute("data-broker") || ""]
    ])

    const parsed = await this.#getJson(query)
    if (!parsed) return

    const statusEl = this.element.querySelector("#help_index_status")
    if (statusEl) {
      statusEl.textContent = parsed.status || ""
      statusEl.classList.remove("hide")
    }

    const widgetEl = this.element.querySelector("#consumer_brokers_widget")
    if (widgetEl) {
      widgetEl.innerHTML = this.sanitize(parsed.broker || "")
    }
  }

  async #selectMyExpert(trigger) {
    const isAssister = trigger.classList.contains("select-assister")
    const selectionRoot = isAssister
      ? trigger.closest(".assister_selection_choice")
      : trigger.closest(".broker_selection_choice")
    if (!selectionRoot) {
      return
    }

    // Only the clicked button: there are many .select-assister nodes (one per listed expert).
    // Do not add .disabled to every list row — that left non-first assisters with a stuck
    // "disabled" button after a prior action (querySelector #active_*_tab only matched the first).
    trigger.classList.add("disabled")

    const helperType = trigger.getAttribute("data-assister") ? "assister" : "broker"
    const query = this.#personPayloadSearchParams([
      ["my_expert", "true"],
      ["assister", trigger.getAttribute("data-assister") || ""],
      ["broker", trigger.getAttribute("data-broker") || ""]
    ])

    const parsed = await this.#getJson(query)
    if (!parsed) {
      trigger.classList.remove("disabled")
      return
    }

    if (!parsed.ok) {
      trigger.classList.remove("disabled")
      this.#applyMyExpertError(helperType, parsed.status || "", selectionRoot)
      return
    }

    this.#applyMyExpertSuccess(helperType, parsed.status || "", selectionRoot)
  }

  #applyMyExpertError(helperType, status, selectionRoot) {
    const tab = selectionRoot.querySelector(`#active_${helperType}_tab`)
    if (!tab) return

    const bodyEl = tab.querySelector(".warning-body")
    if (bodyEl) bodyEl.textContent = status
  }

  #applyMyExpertSuccess(helperType, status, selectionRoot) {
    const form = this.element.querySelector("#inbox_provider_form")
    if (form) form.style.display = "none"

    const tab = selectionRoot.querySelector(`#active_${helperType}_tab`)
    if (!tab) return

    const alertEl = tab.querySelector(".alert")
    if (alertEl) {
      alertEl.classList.remove("alert-warning")
      alertEl.classList.add("alert-success")
    }

    const iconEl = tab.querySelector(".icon")
    if (iconEl) {
      iconEl.classList.remove("warning-icon")
      iconEl.classList.add("success-icon")
    }

    const bodyEl = tab.querySelector(".warning-body")
    if (bodyEl) bodyEl.textContent = status

    // Scope to this expert's detail panel: multiple panels share the same id in the DOM
    // (e.g. #active_assister_tab per row) — never unhide or toggle elements globally.
    selectionRoot.querySelectorAll("a.go-to-expert, button").forEach((el) => {
      el.classList.remove("hidden")
      el.classList.remove("hide")
    })
    tab.querySelectorAll("button").forEach((el) => el.classList.add("hide"))
  }

  #personPayload() {
    return {
      person: this.#htmlById("help_requestor"),
      email: this.#htmlById("help_requestor_email"),
      first_name: this.#valueById("person_first_name"),
      last_name: this.#valueById("person_last_name"),
      ssn: this.#valueById("person_ssn"),
      dob: this.#valueById("jq_datepicker_ignore_person_dob")
    }
  }

  #personPayloadSearchParams(extraPairs = []) {
    const p = new URLSearchParams()
    extraPairs.forEach(([k, v]) => p.set(k, v))
    const fields = this.#personPayload()
    Object.keys(fields).forEach((key) => p.set(key, fields[key]))
    return p
  }

  /**
   * Stimulus 1.x (this project) does not support Stimulus 2+ value defaults.
   * Read the URL from the modal's data attribute, with a safe absolute fallback.
   */
  #requestHelpUrlPrefix() {
    const fromDom = this.element.getAttribute("data-help-me-sign-up-request-help-url-value")
    if (fromDom && fromDom.trim() !== "") return fromDom
    return "/exchanges/hbx_profiles/request_help.html?"
  }

  #requestHelpUrlWithQuery(params) {
    const prefix = this.#requestHelpUrlPrefix()
    const query = params.toString()
    if (!query) return prefix
    if (prefix.endsWith("?")) return `${prefix}${query}`
    return `${prefix}?${query}`
  }

  async #getJson(params) {
    try {
      const response = await fetch(this.#requestHelpUrlWithQuery(params), {
        method: "GET",
        headers: {
          "X-Requested-With": "XMLHttpRequest",
          Accept: "text/html"
        },
        credentials: "same-origin"
      })
      const text = await response.text()
      try {
        return { ...JSON.parse(text), ok: response.ok }
      } catch (_parseError) {
        // If Rails returns an HTML error/redirect page, surface something actionable.
        const status = response.ok ? "Unexpected response from server." : `Request failed (${response.status}).`
        return { status, ok: false }
      }
    } catch (_e) {
      return { status: "Network error. Please try again.", ok: false }
    }
  }

  #toggleId(id, show) {
    const el = this.element.querySelector(`#${id}`)
    if (!el) return
    if (show) {
      el.classList.remove("hide")
      el.classList.remove("d-none")
    } else {
      el.classList.add("hide")
      el.classList.add("d-none")
    }
  }

  /** Plain text only — avoids XSS from API-derived strings (Bearer CWE-79). */
  #setTextContent(id, value) {
    const el = this.element.querySelector(`#${id}`)
    if (!el) return
    el.textContent = value
  }

  #valueById(id) {
    const el = document.getElementById(id)
    return el ? el.value : ""
  }

  /** Hidden fields use plain text; avoid reading innerHTML (Bearer / XSS hygiene). */
  #htmlById(id) {
    const el = document.getElementById(id)
    return el ? el.textContent : ""
  }

  sanitize(html) {
    const allowedTags = sanitizeHtml.defaults.allowedTags.concat([
      'div', 'table', 'thead', 'tbody', 'tr', 'th', 'td',
      'a', 'span', 'ul', 'li', 'nav', 'select', 'option',
      'button', 'span'
    ]);

    return sanitizeHtml(html, {
      allowedTags: allowedTags,
      allowedAttributes: {
        '*': ['class', 'id', 'aria-*', 'data-*'],
        'a': ['href', 'class', 'aria-*', 'data-*'],
        'select': ['class', 'data-*', 'data-target', 'data-action'],
        'option': ['value', 'selected'],
        'button': ['type', 'class', 'data-*'],
        'tr': ['class', 'colspan'],
        'td': ['class', 'colspan']
      }
    });
  }
}
